import { ECONOMY } from '@/config/economy';
import { FEEDBACK } from '@/config/feedback';
import { PLAYER } from '@/config/player';
import { WEAPONS } from '@/config/weapons';
import { updateRecords } from '@/core/records';
import { t } from '@/i18n';
import type { Player } from '@/entities/player';
import type { WeaponHolder } from '@/entities/weapons';
import type { InputManager } from '@/input/input';
import { PlayerStats } from '@/upgrades/stats';
import { cardName } from '@/upgrades/cards';
import type { World } from '@/scene/world';
import { RARITY_COLOR, cardTint } from '@/ui/card-icons';
import type { GameOverScreen } from '@/ui/gameover';
import type { SacrificeItem } from '@/ui/gameover-cards';
import type { Hud } from '@/ui/hud';
import type { RecordToasts } from '@/ui/record-toasts';
import { CATEGORY_COLOR } from '@/ui/shop-cards';
import type { StatView } from '@/ui/stats-panel';
import type { TitleScreen } from '@/ui/title-screen';
import type { UpgradeScreen } from '@/ui/upgrade-screen';
import { sfx } from '@/audio/sfx';
import type { RunState } from './run-state';
import { difficultyFor } from './sector-hooks';
import { inventoryCap, rebuildFromCards } from './ship';
import type { ScreenSize, ShopFlow } from './shop-flow';

/** Overlays the death flow drives. */
export interface DeathScreens {
  readonly gameOver: GameOverScreen;
  readonly shop: UpgradeScreen;
  readonly title: TitleScreen;
}

/**
 * Damage intake + death. ENDLESS runs keep the checkpoint death (docs/12
 * C4/D5/#6): the upgrade bay opens with the sacrifice picker on top, the
 * death tax burns cards/a weapon, and CONTINUE retries the SAME room.
 * SECTOR runs are roguelike: death ends the sector (`sectorLost`, docs/01).
 */
export class DeathFlow {
  constructor(
    private readonly world: World,
    private readonly run: RunState,
    private readonly shop: ShopFlow,
    private readonly screens: DeathScreens,
    private readonly hud: Hud,
    private readonly input: InputManager,
    private readonly toasts: RecordToasts,
    private readonly view: ScreenSize,
    private readonly save: () => void,
    /** Sector run death → the sector is lost (run/sector-flow.ts) */
    private readonly sectorLost: () => void,
  ) {}

  /** The ship takes a hit (i-frames gate it); a fatal one starts the death flow. */
  hitPlayer(damage: number): void {
    const { player, damageNumbers } = this.world;
    // i-frames gate hits; a won finale can't be lost during its victory blast
    if (player.iframes > 0 || this.run.victoryTimer > 0) return;
    this.run.bossHits++;
    const dmg = damage * difficultyFor(this.run).enemyDamage;
    const hadArmor = player.armor > 0;
    const shown = player.mitigated(dmg); // defense cards (docs/12 C5)
    const died = player.takeHit(dmg);
    // Armor ate the hit — no red number, the yellow counter dropping says it
    if (!hadArmor) damageNumbers.spawnHurt(player.x, player.y, shown);
    sfx.playerHit();
    this.input.rumble(...FEEDBACK.playerHit.rumble);
    this.world.camera.shake(FEEDBACK.playerHit.shake);
    this.world.fx.hurt(player.x, player.y);
    if (died) this.onDeath();
  }

  private onDeath(): void {
    const { world, run } = this;
    const player = world.player;
    world.fx.bigExplosion(player.x, player.y);
    if (run.mode === 'sector') {
      this.sectorLost(); // roguelike: no checkpoint, no tax (docs/01)
      return;
    }
    run.totalRuns++;
    run.stats.bulletsFired = world.bullets.friendlyFired;
    const improved = updateRecords(run.stats);

    // Death tax (docs/12 C4): mandatory burns scale with the collection,
    // candidates are real cards — plus a weapon once the tax hits 2 (F3)
    const taxCfg = ECONOMY.deathTax;
    const size = run.ownedCards.length;
    const tax = size === 0 ? 0 : size < taxCfg.sizeTiers[0] ? 1
      : size < taxCfg.sizeTiers[1] ? 2 : 3;
    const indices = run.ownedCards.map((_, i) => i);
    run.rng.shuffle(indices);
    run.discardOffer = indices.slice(0, Math.min(size, taxCfg.candidates));
    const items: SacrificeItem[] = run.discardOffer.map((ownedIdx, offerIdx) => {
      const card = run.ownedCards[ownedIdx]!;
      return {
        kind: 'card' as const, offerIndex: offerIdx, card, weaponId: null,
        level: run.ownedCards.filter((c) => c.id === card.id).length,
        name: cardName(card),
        frame: RARITY_COLOR[card.rarity], accent: cardTint(card),
        armorBonus: taxCfg.burnArmor[card.rarity] ?? 1,
      };
    });
    run.deathWeaponId = null;
    if (tax >= taxCfg.weaponTaxMin && run.inventory.length >= 2) {
      run.deathWeaponId = run.rng.pick(run.inventory);
      const wdef = WEAPONS[run.deathWeaponId]!;
      items.push({
        kind: 'weapon', offerIndex: -1, card: null, level: 1,
        weaponId: run.deathWeaponId,
        name: t(wdef.nameKey as Parameters<typeof t>[0]),
        frame: RARITY_COLOR[wdef.rarity], accent: CATEGORY_COLOR[wdef.category]!,
        armorBonus: taxCfg.weaponBurnArmor,
      });
    }

    // Death flow rework (docs/12 #6): open the UPGRADE BAY first, then float
    // the sacrifice picker ON TOP. After paying the tax the player stays in
    // the bay (swap weapons, buy) and CONTINUE restarts the SAME arena.
    run.deathShop = true;
    run.nextPick = run.currentFight; // preview shows the boss being re-fought
    this.shop.open();
    this.screens.gameOver.show(this.view.width, this.view.height, {
      stats: run.stats, improved, seed: run.seed, totalRuns: run.totalRuns,
      tax, items, statsView: (ex) => this.statsView(ex),
    });
  }

  /** Ship stats rebuilt WITHOUT the given card offers — removal preview. */
  private statsView(excludedOffers: ReadonlyArray<number>): StatView {
    const run = this.run;
    const excludedOwned = new Set(excludedOffers.map((i) => run.discardOffer[i]!));
    const cards = run.ownedCards.filter((_, i) => !excludedOwned.has(i));
    const stats = new PlayerStats();
    const weapon = { gunTier: 1, rackSlots: 0 } as WeaponHolder;
    const player = { hp: this.world.player.hp } as Player;
    const owned = (id: string) => cards.filter((c) => c.id === id).length;
    for (const card of cards) card.apply({ stats, weapon, player, owned });
    return { stats, gunTier: weapon.gunTier, hp: player.hp };
  }

  /**
   * Death tax resolution (docs/12 #6): burn the chosen cards/weapon and rebuild
   * the ship, then either return to the title OR stay in the upgrade bay (the
   * sacrifice picker just closes and the bay behind it takes over). The arena
   * itself only reloads when the player presses CONTINUE (RunFlow.continueRun).
   */
  resolve(
    burnedOffers: number[], weaponBurned: boolean, extraArmor: number, toMenu: boolean,
  ): void {
    const { run, world } = this;
    const ownedIdxs = burnedOffers
      .map((i) => run.discardOffer[i])
      .filter((i): i is number => i !== undefined)
      .sort((a, b) => b - a);
    for (const idx of ownedIdxs) run.ownedCards.splice(idx, 1);
    if (weaponBurned && run.deathWeaponId !== null && run.inventory.length > 1) {
      const k = run.inventory.indexOf(run.deathWeaponId);
      if (k >= 0) run.inventory.splice(k, 1);
    }
    run.deathWeaponId = null;
    this.rebuildShip();
    // Burns past the tax repaired armor — applied AFTER the ship rebuild
    if (extraArmor > 0) {
      world.player.armor = Math.min(PLAYER.maxArmor, world.player.armor + extraArmor);
      this.hud.flashArmor();
    }
    this.screens.gameOver.hide();
    if (toMenu) {
      run.deathShop = false;
      run.nextPick = null; // drop the retry-preview pick
      this.screens.shop.hide();
      this.save();
      this.screens.title.show(this.view.width, this.view.height);
    } else {
      // Stay in the upgrade bay — reflect the burned cards + full-HP respawn
      this.shop.refresh();
      this.save();
    }
  }

  /**
   * Checkpoint death (docs/12 D5): the run does NOT reset. Rebuild the ship
   * from the surviving collection (full-HP respawn); gems, inventory and room
   * progress all carry over.
   */
  private rebuildShip(): void {
    const { run, world } = this;
    const player = world.player;
    rebuildFromCards(player, run, [...player.weapon.equipped]);
    // If a sacrificed Cargo Bay card shrank the bays, drop unequipped extras
    for (let i = run.inventory.length - 1; run.inventory.length > inventoryCap(player) && i >= 0; i--) {
      const id = run.inventory[i]!;
      if (!player.weapon.equipped.includes(id)) {
        run.inventory.splice(i, 1);
      }
    }
    this.toasts.reset();
  }
}
